import { useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

// axial hex helpers
const neighbors = [
  [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1],
];
function coordKey(q, r) { return `${q},${r}`; }
function uniqueNeighbors(q, r, used) {
  const out = [];
  for (const [dq, dr] of neighbors) {
    const k = coordKey(q + dq, r + dr);
    if (!used.has(k)) out.push([q + dq, r + dr]);
  }
  return out;
}

export default function useMindmapSession() {
  const [sessionId, setSessionId] = useState(null);
  const [payload, setPayload] = useState({
    mode: "mindmap",
    ui: "text",
    body: null,
    chips: [],
    statement: "What’s on your mind?",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [turning, setTurning] = useState(false);

  // graph state
  const [current, setCurrent] = useState([0, 0]);
  const [visited, setVisited] = useState(new Set([coordKey(0, 0)]));
  const [candidates, setCandidates] = useState([]);
  const [chipMap, setChipMap] = useState({});

  // --- Session helpers ---
  async function startNewSession(seed = {}) {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/frameworks/mindmap/session/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(seed),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      hydrateFromServer(data);
    } catch (e) {
      console.error(e);
      setError("Could not start session.");
    } finally {
      setLoading(false);
    }
  }

  function rehydrateSession(sessionData) {
    if (!sessionData) return;
    setSessionId(sessionData.session_id);

    // If backend gave us a last_turn, use it for payload
    if (sessionData.last_turn) {
      setPayload({
        session_id: sessionData.session_id,
        rolling_summary: sessionData.rolling_summary,
        ui: sessionData.last_turn.ui,
        body: sessionData.last_turn.body_text,
        chips: sessionData.last_turn.chips_json || [],
        strategy_hint: sessionData.last_turn.strategy_hint,
        assistant_prompt_text: sessionData.last_turn.assistant_prompt_text,
        user_text: sessionData.last_turn.user_text,
      });
    } else {
      setPayload({
        session_id: sessionData.session_id,
        rolling_summary: sessionData.rolling_summary,
        ui: "text",
        body: null,
        chips: [],
      });
    }

    setCurrent([0, 0]);
    setVisited(new Set([coordKey(0, 0)]));
    setCandidates([]);
    setChipMap({});
  }

  function hydrateFromServer(data) {
    setSessionId(data.session_id);
    setPayload(data);
    setCurrent([0, 0]);
    setVisited(new Set([coordKey(0, 0)]));
    setCandidates([]);
    setChipMap({});
  }

  // --- Turn handling ---
  async function submitText(text) {
    try {
      setTurning(true);
      const used = new Set(Array.from(visited));
      used.add(coordKey(...current));
      const nexts = uniqueNeighbors(current[0], current[1], used);
      const next = nexts.length ? nexts[0] : [current[0] + 1, current[1]];
      setVisited(prev => new Set(prev).add(coordKey(...current)));
      setCurrent(next);
      setCandidates([]);
      setChipMap({});

      const resp = await fetch(`${API_BASE_URL}/frameworks/mindmap/turn`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_action: { type: "text", value: text },
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setPayload(data);
    } catch (e) {
      console.error(e);
      setError("Turn failed.");
    } finally {
      setTurning(false);
    }
  }

  async function submitChip(value) {
    try {
      setTurning(true);
      const coord = chipMap[value];
      if (coord) {
        setVisited(prev => new Set(prev).add(coordKey(...current)));
        setCurrent(coord);
      }
      const resp = await fetch(`${API_BASE_URL}/frameworks/mindmap/turn`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_action: { type: "chip", value },
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setPayload(data);
    } catch (e) {
      console.error(e);
      setError("Turn failed.");
    } finally {
      setCandidates([]);
      setChipMap({});
      setTurning(false);
    }
  }

  const uiMode = payload?.ui || "text";

  return {
    sessionId,
    payload,
    loading,
    turning,
    error,
    submitText,
    submitChip,
    rehydrateSession,
    startNewSession,
    graph: { current, visited, candidates, uiMode },
  };
}
